/**
 * Add / Edit transaction dialog. The biggest single chunk of UI in the
 * old Transactions.js — owns the entire conditional form:
 *   - mechanic toggle (one-off vs recurring), locked when editing
 *   - transfer toggle, locked when editing
 *   - single-account vs transfer source/destination fields
 *   - description / amount / type (with liability-aware hint)
 *   - one-off date OR recurring frequency + start + end (+ UK working-day
 *     notice)
 *   - category + estimate toggle
 *   - submit / cancel
 *
 * Pure view: the shell passes formData + setFormData + submit handler.
 */
import React from 'react';
import { ArrowLeftRight, Receipt as ReceiptIcon, Repeat } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Button } from '../../components/ui/button';

const TransactionDialog = ({
  open, onOpenChange,
  editingItem, formData, setFormData,
  accounts, formIsLiability,
  handleSubmit,
}) => {
  const isRecurring = formData.mechanic === 'recurring';
  const dialogTitle = !editingItem
    ? 'Add Transaction'
    : (editingItem.mechanic === 'recurring' ? 'Edit Recurring' : 'Edit Transaction');
  const submitLabel = editingItem ? 'Update' : (isRecurring ? 'Create Recurring' : 'Add Transaction');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#020617]">{dialogTitle}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="transaction-form">
          <div>
            <Label>Frequency</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="mechanic-toggle">
              <button
                type="button"
                disabled={!!editingItem}
                onClick={() => setFormData({ ...formData, mechanic: 'one_off' })}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                  !isRecurring ? 'bg-[#0052FF] text-white border-[#0052FF]' : 'bg-white text-[#020617] border-[#E2E8F0] hover:bg-[#F8FAFC]'
                } ${editingItem ? 'opacity-60 cursor-not-allowed' : ''}`}
                data-testid="mechanic-one-off-button"
              >
                <ReceiptIcon className="w-4 h-4" />
                One-off
              </button>
              <button
                type="button"
                disabled={!!editingItem}
                onClick={() => setFormData({ ...formData, mechanic: 'recurring' })}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                  isRecurring ? 'bg-[#0052FF] text-white border-[#0052FF]' : 'bg-white text-[#020617] border-[#E2E8F0] hover:bg-[#F8FAFC]'
                } ${editingItem ? 'opacity-60 cursor-not-allowed' : ''}`}
                data-testid="mechanic-recurring-button"
              >
                <Repeat className="w-4 h-4" />
                Recurring
              </button>
            </div>
            {editingItem && (
              <p className="text-xs text-[#64748B] mt-1">Frequency can&apos;t be changed after creation. Delete and recreate to swap.</p>
            )}
          </div>

          <div className="flex items-start gap-3 p-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC]" data-testid="transfer-toggle">
            <Checkbox
              id="is_transfer"
              checked={!!formData.is_transfer}
              disabled={!!editingItem}
              onCheckedChange={(checked) => setFormData({ ...formData, is_transfer: !!checked })}
              className="mt-0.5"
              data-testid="transfer-checkbox"
            />
            <div className="flex-1">
              <Label htmlFor="is_transfer" className="cursor-pointer text-sm font-medium text-[#020617] inline-flex items-center gap-2">
                <ArrowLeftRight className="w-3.5 h-3.5" />
                Account-to-account transfer
              </Label>
              <p className="text-xs text-[#64748B] mt-0.5">
                Moves money between two of your accounts (e.g. current → savings, or a loan payment). Both sides update automatically.
              </p>
            </div>
          </div>

          {!formData.is_transfer && (
            <div>
              <Label>Account</Label>
              <Select value={formData.account_id} onValueChange={(value) => setFormData({ ...formData, account_id: value })}>
                <SelectTrigger className="mt-1.5" data-testid="transaction-account-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc.account_id} value={acc.account_id}>
                      {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {formData.is_transfer && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="transfer-accounts">
              <div>
                <Label>From account</Label>
                <Select
                  value={formData.source_account_id}
                  onValueChange={(value) => setFormData({ ...formData, source_account_id: value })}
                  disabled={!!editingItem}
                >
                  <SelectTrigger className="mt-1.5" data-testid="transfer-source-select">
                    <SelectValue placeholder="Pick source" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((acc) => (
                      <SelectItem key={acc.account_id} value={acc.account_id} disabled={acc.account_id === formData.destination_account_id}>
                        {acc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>To account</Label>
                <Select
                  value={formData.destination_account_id}
                  onValueChange={(value) => setFormData({ ...formData, destination_account_id: value })}
                  disabled={!!editingItem}
                >
                  <SelectTrigger className="mt-1.5" data-testid="transfer-destination-select">
                    <SelectValue placeholder="Pick destination" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((acc) => (
                      <SelectItem key={acc.account_id} value={acc.account_id} disabled={acc.account_id === formData.source_account_id}>
                        {acc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editingItem && (
                <p className="text-xs text-[#64748B] sm:col-span-2">Transfer accounts can&apos;t be changed after creation. Delete and recreate to redirect.</p>
              )}
            </div>
          )}

          <div>
            <Label htmlFor="description">Description{formData.is_transfer ? ' (Optional)' : ''}</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required={!formData.is_transfer}
              placeholder={formData.is_transfer ? 'e.g. Monthly savings sweep (auto-filled if left blank)' : (isRecurring ? 'Monthly rent' : 'Grocery shopping')}
              className="mt-1.5"
              data-testid="transaction-description-input"
            />
          </div>

          <div>
            <Label htmlFor="amount">Amount (GBP)</Label>
            <Input id="amount" type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })} required className="mt-1.5" data-testid="transaction-amount-input" />
          </div>

          {!formData.is_transfer && (
            <div>
              <Label>Type</Label>
              <Select value={formData.transaction_type} onValueChange={(value) => setFormData({ ...formData, transaction_type: value })}>
                <SelectTrigger className="mt-1.5" data-testid="transaction-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
              {formIsLiability && (
                <p
                  className="text-[11px] text-[#64748B] mt-1.5 leading-relaxed"
                  data-testid="transaction-type-liability-hint"
                >
                  <span className="font-semibold text-[#334155]">Income</span> reduces this debt (e.g. payment, refund). <span className="font-semibold text-[#334155]">Expense</span> adds to it (e.g. interest, charge, new purchase).
                </p>
              )}
            </div>
          )}

          {!isRecurring && (
            <div>
              <Label htmlFor="date">Date</Label>
              <Input id="date" type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required className="mt-1.5" data-testid="transaction-date-input" />
            </div>
          )}

          {isRecurring && (
            <>
              <div>
                <Label>Frequency</Label>
                <Select value={formData.frequency} onValueChange={(value) => setFormData({ ...formData, frequency: value })}>
                  <SelectTrigger className="mt-1.5" data-testid="recurring-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="every_4_weeks">Every 4 Weeks</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="first_working_day_of_month">1st working day of month</SelectItem>
                    <SelectItem value="last_working_day_of_month">Last working day of month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 text-xs text-[#64748B]" data-testid="uk-holiday-notice">
                <strong className="text-[#020617]">UK Banking:</strong> Standard frequencies shift weekends and bank holidays to the previous working day. &quot;1st working day&quot; shifts forward to the next working day; &quot;Last working day&quot; shifts backward.
              </div>
              <div>
                <Label htmlFor="start_date">Start Date</Label>
                <Input id="start_date" type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required className="mt-1.5" data-testid="recurring-start-date-input" />
              </div>
              <div>
                <Label htmlFor="end_date">End Date (Optional)</Label>
                <Input id="end_date" type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} className="mt-1.5" data-testid="recurring-end-date-input" />
              </div>
            </>
          )}

          <div>
            <Label htmlFor="category">Category (Optional)</Label>
            <Input id="category" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} placeholder="Food, Transport, etc." className="mt-1.5" data-testid="transaction-category-input" />
          </div>

          <div className="flex items-start gap-3 p-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC]" data-testid="estimated-toggle">
            <Checkbox
              id="is_estimated"
              checked={!!formData.is_estimated}
              onCheckedChange={(checked) => setFormData({ ...formData, is_estimated: !!checked })}
              className="mt-0.5"
              data-testid="estimated-checkbox"
            />
            <div className="flex-1">
              <Label htmlFor="is_estimated" className="cursor-pointer text-sm font-medium text-[#020617]">
                This is an estimate
              </Label>
              <p className="text-xs text-[#64748B] mt-0.5">
                Tick if the amount is approximate (e.g. variable bills). Forecasts will flag months that include estimates.
              </p>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" className="flex-1 bg-[#0052FF] text-white hover:bg-[#003ECC]" data-testid="transaction-submit-button">
              {submitLabel}
            </Button>
            <Button type="button" onClick={() => onOpenChange(false)} className="flex-1 bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]">
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionDialog;
